import { merge } from 'lodash';
import React, { useMemo, useCallback } from 'react';

import { UserSortFields, SortDirection, GetUsersQueryVariables } from '@/graphql';
import { BaseTableProps } from '@boilerplate/types/entity';
import Entity from '@/entities/user';
import EntityTable, {
  defaultPage,
  defaultPageSize,
  defaultSortOrder,
  getDefaultSortField,
} from '@boilerplate/components/entity/EntityTable/EntityTable';
import { canPerformAction, useAuthenticatedUserStore } from '@/stores/UserStore';
import { openConfirmationDialog } from '@/stores/ConfirmationDialogStore';


export const useUsersTable = ({ queryOptions }: { queryOptions?: GetUsersQueryVariables } = {}) => {
  const defaultVariableOptions = useMemo<GetUsersQueryVariables>(
    () => 
      merge(
        {
          sorting: {
            direction: defaultSortOrder === 'desc' ? SortDirection.Desc : SortDirection.Asc,
            field: getDefaultSortField(Entity.table.schema) as UserSortFields,
          },
          paging: {
            page: defaultPage,
            pageSize: defaultPageSize,
          },
          },
          queryOptions,
        ), 
    [queryOptions]
    );
  
  const {
    data: userData,
    items = [],
    loading,
    refetch: originalRefetch,
    variables,
  } = Entity.model.useGetAll({
    fetchPolicy: 'cache-and-network',
    variables: defaultVariableOptions,
  });

  const [remove] = Entity.model.useDelete();

  const refetch = useCallback(
    (variableOptions?: Partial<GetUsersQueryVariables >) => {
      return originalRefetch(merge({}, defaultVariableOptions, variableOptions));
    },
    [defaultVariableOptions, originalRefetch]
  );

  const allActions = useMemo(() => ({ loading, refetch, remove }), [loading, refetch, remove]);
  const data = useMemo(
    () =>
      userData
        ? { totalCount: userData.users.totalCount, rows: items }
        : { totalCount: 0, rows: [] },
    [userData, items]
  );

  return { data, actions: allActions, refetch, defaultVariableOptions, variables };
}

export type UsersBaseTableProps = BaseTableProps<typeof Entity.model>;

export default function UsersBaseTable(props: UsersBaseTableProps) {
  const tableProps = useUsersTable();
  const authentictedUser = useAuthenticatedUserStore();

  return (
    <EntityTable
      {...props}
      {...tableProps}
      Entity={Entity}
      user={authentictedUser}
      canPerformThisAction={canPerformAction}
      openConfirmationDialog={openConfirmationDialog}
    />
  );
}
